/* eslint-disable no-console */
import { useShowNotification, useSocket } from '@elotech/components';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import React from 'react';

import { AsyncTaskModal, SILENT_REJECTION } from '../components';
import { createAsyncTaskService } from '../service';
import { AsyncTask } from '../type';

export type AsyncTaskContextValues = {
  waitAsyncTask<T>(task: AsyncTask): Promise<T>;
};
export const AsyncTaskContext = createContext<AsyncTaskContextValues>(
  {} as AsyncTaskContextValues
);

type AsyncTaskContextProviderProps = {
  children: React.ReactNode;
  baseUrl: string;
};

export const useAsyncTask = () => useContext(AsyncTaskContext);

export const AsyncTaskContextProvider = ({
  children,
  baseUrl
}: AsyncTaskContextProviderProps) => {
  const showNotification = useShowNotification();
  const [asyncTask, setAsyncTask] = useState<AsyncTask>();
  const [resolver, setResolver] = useState<(value: any) => void>();
  const [rejecter, setRejecter] = useState<(reason?: any) => void>();
  const { cancelAsyncTask, getTaskResult } = useMemo(
    () => createAsyncTaskService(baseUrl),
    [baseUrl]
  );

  const socket = useSocket();

  const waitAsyncTask = useCallback(<T,>(task: AsyncTask): Promise<T> => {
    setAsyncTask(task);
    return new Promise<T>((resolve, reject) => {
      setResolver(() => resolve);
      setRejecter(() => reject);
    });
  }, []);

  const handler = useCallback(
    (message: any) => {
      let asyncTaskReceived: AsyncTask;
      try {
        asyncTaskReceived = message?.id ? message : JSON.parse(message[0]);
      } catch (e) {
        window.console.warn('Falha ao desserializar mensagem', e);
        return;
      }

      console.debug(
        'Mensagem recebida na tarefa assíncrona',
        asyncTaskReceived,
        asyncTask
      );
      if (asyncTaskReceived?.externalKey === asyncTask?.externalKey) {
        setAsyncTask(asyncTaskReceived);
      }
    },
    [asyncTask?.externalKey]
  );

  useEffect(() => {
    if (!socket || !asyncTask?.externalKey) {
      return;
    }

    const topic = asyncTask.externalKey;
    socket.on(topic, handler);
    console.debug(`Inscrito na tarefa assíncrona ${topic}`);

    return () => {
      socket.off(topic, handler);
      console.debug(`Desinscrito da tarefa assíncrona ${topic}`);
    };
  }, [socket, asyncTask?.externalKey, handler]);

  useEffect(() => {
    if (asyncTask?.cancelled) {
      rejecter?.(new Error(SILENT_REJECTION));
      setAsyncTask(undefined);
      showNotification({
        level: 'warning',
        message: 'Operação cancelada pelo usuário.'
      });
      return;
    }

    if (asyncTask?.finished) {
      getTaskResult(asyncTask)
        .then(response => {
          console.debug('Tarefa assíncrona finalizada', response);
          resolver?.(response.data);
        })
        .catch(error => {
          console.error('Erro na tarefa assíncrona', error);
          rejecter?.(error);
        })
        .finally(() => setAsyncTask(undefined));
      return;
    }

    if (!asyncTask) {
      setResolver(undefined);
      setRejecter(undefined);
    }
  }, [asyncTask]);

  const onCancel = () =>
    cancelAsyncTask(asyncTask!).then(() => {
      rejecter?.(new Error(SILENT_REJECTION));
      setAsyncTask(undefined);
    });

  return (
    <AsyncTaskContext.Provider value={{ waitAsyncTask }}>
      <AsyncTaskModal asyncTask={asyncTask} onCancel={onCancel} />
      {children}
    </AsyncTaskContext.Provider>
  );
};
